import React, { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Forward, Heart, MapPin, MessageCircle } from "lucide-react";
import API_BASE from "../../api";
import {
  isVideoPostMedia,
  resolvePostMediaSrc,
} from "../../utils/postMedia";
import { getShareCountForPost } from "../../utils/postShares";
import PostInfoActionIcon from "../post/PostInfoActionIcon";
import PaginationSentinel from "../common/PaginationSentinel";
import TranslatablePostCaption from "../post/TranslatablePostCaption";

const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/847/847969.png";
const BADGE_BASE_PATH = "/badges/";
const BADGE_EXTENSION = ".png";
const USER_PROFILE_VIDEO_BACKDROP_CLOSE_GUARD_MS = 650;
const DEBUG_USER_PROFILE_VIDEO = false;
const USER_PROFILE_VIDEO_POSTER_CANDIDATES = [
  ["poster"],
  ["posterUrl"],
  ["poster_url"],
  ["thumbnail"],
  ["thumbnailUrl"],
  ["thumbnail_url"],
  ["previewUrl"],
  ["preview_url"],
  ["imageUrl"],
  ["image_url"],
  ["coverUrl"],
  ["cover_url"],
  ["media", "poster"],
  ["media", "posterUrl"],
  ["media", "poster_url"],
  ["media", "thumbnail"],
  ["media", "thumbnailUrl"],
  ["media", "thumbnail_url"],
  ["media", "previewUrl"],
  ["media", "preview_url"],
  ["media", "imageUrl"],
  ["media", "image_url"],
  ["media", "coverUrl"],
  ["media", "cover_url"],
  ["videoMetadata", "poster"],
  ["videoMetadata", "posterUrl"],
  ["videoMetadata", "poster_url"],
  ["videoMetadata", "thumbnail"],
  ["videoMetadata", "thumbnailUrl"],
  ["videoMetadata", "thumbnail_url"],
  ["videoMetadata", "previewUrl"],
  ["videoMetadata", "preview_url"],
  ["videoMetadata", "coverUrl"],
  ["videoMetadata", "cover_url"],
];

function shortenUserProfileVideoUrl(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  try {
    const baseUrl =
      typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const parsed = new URL(rawValue, baseUrl);
    const pathParts = parsed.pathname.split("/").filter(Boolean);
    const shortPath = pathParts.slice(-3).join("/");
    return shortPath || parsed.pathname || parsed.hostname;
  } catch {
    const withoutQuery = rawValue.split(/[?#]/)[0];
    return withoutQuery.length > 96
      ? `...${withoutQuery.slice(-96)}`
      : withoutQuery;
  }
}

function getUserProfileVideoDebugSnapshot(video) {
  if (!video) {
    return { hasVideo: false };
  }

  const error = video.error
    ? {
        code: video.error.code,
        message: video.error.message || "",
      }
    : null;

  return {
    hasVideo: true,
    src: shortenUserProfileVideoUrl(video.currentSrc || video.src),
    readyState: video.readyState,
    networkState: video.networkState,
    paused: video.paused,
    currentTime: Number.isFinite(video.currentTime)
      ? Number(video.currentTime.toFixed(3))
      : video.currentTime,
    error,
  };
}

function getUserProfileVideoEventPreviewVideo(event) {
  const currentTarget = event?.currentTarget;
  if (!currentTarget) return null;
  if (currentTarget.tagName === "VIDEO") return currentTarget;
  if (typeof currentTarget.querySelector === "function") {
    return currentTarget.querySelector(".rp-post__media--video");
  }
  return null;
}

function debugUserProfileVideo(message, data = {}) {
  if (!DEBUG_USER_PROFILE_VIDEO || typeof console === "undefined") return;
  console.debug("[UserProfileVideoDebug]", message, data);
}

function getNestedUserProfileVideoValue(source, path) {
  return path.reduce((current, key) => {
    if (!current || typeof current !== "object") return undefined;
    return current[key];
  }, source);
}

function resolveUserProfileVideoPosterUrl(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  if (/^(https?:|data:|blob:)/i.test(rawValue)) return rawValue;
  return `${API_BASE}${rawValue}`;
}

function resolveUserProfileVideoPoster(post) {
  for (const path of USER_PROFILE_VIDEO_POSTER_CANDIDATES) {
    const value = getNestedUserProfileVideoValue(post, path);
    const src = resolveUserProfileVideoPosterUrl(value);
    if (src) {
      return {
        src,
        field: path.join("."),
      };
    }
  }

  return {
    src: "",
    field: "",
  };
}

function normalizeTextValue(value) {
  return String(value || "").trim();
}

function hasMeaningfulValue(value) {
  const normalized = normalizeTextValue(value);
  return Boolean(normalized) && normalized !== "-";
}

function humanizeFeedValue(value) {
  return normalizeTextValue(value)
    .replace(/^[^.]+\./, "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function translateFeedValue(t, value) {
  const normalized = normalizeTextValue(value);
  if (!normalized) return "";
  return t(normalized, { defaultValue: humanizeFeedValue(normalized) });
}

function looksLikeCoordinateLabel(value) {
  return /^-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?$/.test(
    normalizeTextValue(value)
  );
}

function pickLocationLabel(...values) {
  for (const value of values) {
    const normalized = normalizeTextValue(value);
    if (
      normalized &&
      normalized !== "-" &&
      normalized.toLowerCase() !== "unknown" &&
      !looksLikeCoordinateLabel(normalized)
    ) {
      return normalized;
    }
  }

  return "";
}

function pickCountryFromLocationLabel(...values) {
  const label = pickLocationLabel(...values);
  if (!label) return "";

  const parts = label
    .split(",")
    .map(normalizeTextValue)
    .filter(Boolean);

  if (parts.length < 2) return "";

  return parts[parts.length - 1];
}

function getPostLocationLabel(post) {
  const catchData = post?.catch || {};
  if (catchData.isLocationPublic === false) return "";

  const explicitCountry = pickLocationLabel(
    catchData.country,
    post?.country,
    catchData.historicalContext?.country,
    post?.historicalContext?.country
  );

  if (explicitCountry) return explicitCountry;

  return pickCountryFromLocationLabel(
    catchData.locationLabel,
    post?.locationLabel,
    catchData.historicalContext?.locationLabel,
    post?.historicalContext?.locationLabel,
    catchData.placeName,
    post?.placeName,
    catchData.location,
    post?.location
  );
}

function getPostDateLabel(post) {
  if (!post?.postDate) return "";

  const parsedDate = new Date(post.postDate);
  if (Number.isNaN(parsedDate.getTime())) return normalizeTextValue(post.postDate);

  return parsedDate.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function resolveUserAvatarSrc(authorUser) {
  const avatar = normalizeTextValue(authorUser?.avatar);
  if (!avatar) return DEFAULT_AVATAR;
  return /^https?:\/\//i.test(avatar) ? avatar : `${API_BASE}${avatar}`;
}

function normalizeAvatarPosition(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 50;
  return Math.min(100, Math.max(0, parsed));
}

function resolveAvatarObjectPosition(authorUser) {
  const positionX = normalizeAvatarPosition(authorUser?.avatarPositionX);
  const positionY = normalizeAvatarPosition(authorUser?.avatarPositionY);
  return `${positionX}% ${positionY}%`;
}

function resolveAvatarScale(authorUser) {
  const parsed = Number(authorUser?.avatarScale);
  if (!Number.isFinite(parsed) || parsed <= 0) return 1;
  return parsed;
}

function buildBadgeUrl(badgeKey) {
  const normalizedKey = normalizeTextValue(badgeKey).toLowerCase();
  if (!normalizedKey) return "";
  const publicUrl = process.env.PUBLIC_URL || "";
  return `${publicUrl}${BADGE_BASE_PATH}${normalizedKey}${BADGE_EXTENSION}`;
}

function buildPostChips(post, t) {
  const catchData = post?.catch || {};
  const chipSources = [
    catchData.fish,
    catchData.wayOfFishing,
    catchData.typeOfFishing,
  ];
  const seen = new Set();

  return chipSources
    .map((value) => normalizeTextValue(value))
    .filter(hasMeaningfulValue)
    .filter((value) => value.toLowerCase() !== "unknown")
    .map((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return "";
      seen.add(key);
      return translateFeedValue(t, value);
    })
    .filter(Boolean)
    .slice(0, 4);
}

export default function UserProfileContentSections({ profile }) {
  const {
    t,
    user,
    selectedUser,
    selectedUserData,
    userPosts,
    shareCountLookup,
    openComments,
    commentText,
    replyTo,
    replyText,
    usersMap,
    isPostLikePending,
    highlightedCommentKey,
    postRefs,
    commentRefs,
    setCommentText,
    setReplyText,
    handleOpenExpandedMedia,
    handleLikePost,
    handleToggleComments,
    handleOpenShare,
    handleOpenInfo,
    handleLikeComment,
    handleToggleReply,
    handleLikeReply,
    handleSubmitReply,
    handleSubmitComment,
    profilePostsLoadingMore,
    profilePostsHasMore,
    loadMoreProfilePosts,
  } = profile;
  const [zoomVideo, setZoomVideo] = useState(null);
  const [zoomVideoPlayFailed, setZoomVideoPlayFailed] = useState(false);
  const [mobileUserProfileVideoViewport, setMobileUserProfileVideoViewport] =
    useState(
      () =>
        typeof window !== "undefined" &&
        window.matchMedia("(max-width: 768px)").matches
    );
  const [mobilePreviewMetadataVideoIds, setMobilePreviewMetadataVideoIds] =
    useState(() => new Set());
  const userProfilePostsRef = useRef(null);
  const zoomVideoRef = useRef(null);
  const shouldAttemptZoomVideoPlayRef = useRef(false);
  const zoomVideoPlayAttemptInFlightRef = useRef(false);
  const zoomVideoOpenIdRef = useRef(0);
  const zoomVideoOpenedAtRef = useRef(0);
  const mobileVideoPreviewRefs = useRef({});
  const zoomVideoUrl = zoomVideo?.url || "";
  const zoomVideoInstanceId = zoomVideo?.instanceId || 0;

  const pauseVideoElement = useCallback((video, options = {}) => {
    if (!video) {
      return {
        before: getUserProfileVideoDebugSnapshot(video),
        after: getUserProfileVideoDebugSnapshot(video),
        error: null,
      };
    }

    const before = getUserProfileVideoDebugSnapshot(video);
    let errorDetails = null;
    try {
      video.pause();
      if (options.reset) {
        video.currentTime = 0;
      }
    } catch (error) {
      errorDetails = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    return {
      before,
      after: getUserProfileVideoDebugSnapshot(video),
      error: errorDetails,
    };
  }, []);

  const hardStopVideoElement = useCallback((video, reason = "unspecified") => {
    const before = getUserProfileVideoDebugSnapshot(video);
    const beforeAttributeSrc = video?.getAttribute?.("src") || "";
    const beforeSources =
      video && typeof video.querySelectorAll === "function"
        ? Array.from(video.querySelectorAll("source"))
        : [];
    const result = {
      reason,
      before,
      beforeAttributeSrc: shortenUserProfileVideoUrl(beforeAttributeSrc),
      beforeSourceCount: beforeSources.length,
      pauseError: null,
      muteError: null,
      resetError: null,
      clearSourceError: null,
      loadCalled: false,
      loadError: null,
      afterAttributeSrc: "",
      after: null,
    };

    if (!video) return result;

    try {
      video.pause();
    } catch (error) {
      result.pauseError = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    try {
      video.muted = true;
    } catch (error) {
      result.muteError = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    try {
      video.currentTime = 0;
    } catch (error) {
      result.resetError = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    try {
      video.removeAttribute("src");
      beforeSources.forEach((source) => {
        source.removeAttribute("src");
      });
    } catch (error) {
      result.clearSourceError = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    result.afterAttributeSrc = video.getAttribute("src") || "";

    try {
      video.load();
      result.loadCalled = true;
    } catch (error) {
      result.loadError = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    result.after = getUserProfileVideoDebugSnapshot(video);
    return result;
  }, []);

  const cleanupUserProfileVideoMediaSession = useCallback(
    (reason = "unspecified") => {
      if (typeof navigator === "undefined" || !("mediaSession" in navigator)) {
        return;
      }

      try {
        navigator.mediaSession.playbackState = "none";
      } catch (error) {
        debugUserProfileVideo("mediaSession playbackState cleanup failed", {
          reason,
          name: error?.name || "",
          message: error?.message || "",
        });
      }

      try {
        navigator.mediaSession.metadata = null;
      } catch (error) {
        debugUserProfileVideo("mediaSession metadata cleanup failed", {
          reason,
          name: error?.name || "",
          message: error?.message || "",
        });
      }
    },
    []
  );

  const pauseEmbeddedUserProfileVideos = useCallback(
    (options = {}) => {
      if (typeof document === "undefined") return;
      const allVideos = Array.from(
        document.querySelectorAll(
          ".userprofile-posts .rp-profile-post-card .rp-post__media--video"
        )
      );
      const viewerVideo = zoomVideoRef.current;
      allVideos
        .filter((video) => video !== viewerVideo)
        .forEach((video) => pauseVideoElement(video, options));
    },
    [pauseVideoElement]
  );

  const playZoomVideo = useCallback(
    (targetVideo) => {
      const video = targetVideo || zoomVideoRef.current;
      if (!video) return;

      try {
        const playInstanceId = zoomVideoInstanceId;
        const isCurrentPlayAttempt = () =>
          zoomVideoOpenIdRef.current === playInstanceId &&
          zoomVideoRef.current === video;
        const playPromise = video.play();

        if (playPromise && typeof playPromise.catch === "function") {
          playPromise
            .then(() => {
              if (!isCurrentPlayAttempt()) return;
              shouldAttemptZoomVideoPlayRef.current = false;
              zoomVideoPlayAttemptInFlightRef.current = false;
              setZoomVideoPlayFailed(false);
            })
            .catch((error) => {
              if (!isCurrentPlayAttempt()) {
                debugUserProfileVideo(
                  "video.play() rejected for stale viewer",
                  {
                    instanceId: playInstanceId,
                    currentInstanceId: zoomVideoOpenIdRef.current,
                    name: error?.name || "",
                    message: error?.message || "",
                  }
                );
                return;
              }
              shouldAttemptZoomVideoPlayRef.current = false;
              zoomVideoPlayAttemptInFlightRef.current = false;
              setZoomVideoPlayFailed(true);
            });
        } else {
          shouldAttemptZoomVideoPlayRef.current = false;
          zoomVideoPlayAttemptInFlightRef.current = false;
          setZoomVideoPlayFailed(false);
        }
      } catch (error) {
        shouldAttemptZoomVideoPlayRef.current = false;
        zoomVideoPlayAttemptInFlightRef.current = false;
        setZoomVideoPlayFailed(true);
        debugUserProfileVideo("video.play() threw", {
          instanceId: zoomVideoInstanceId,
          name: error?.name || "",
          message: error?.message || "",
          video: getUserProfileVideoDebugSnapshot(video),
        });
      }
    },
    [zoomVideoInstanceId]
  );

  const attemptZoomVideoPlay = useCallback(
    (reason = "unspecified") => {
      if (!shouldAttemptZoomVideoPlayRef.current) return;
      if (zoomVideoPlayAttemptInFlightRef.current) {
        debugUserProfileVideo("play skipped: attempt in flight", { reason });
        return;
      }

      const video = zoomVideoRef.current;
      if (!video) return;

      zoomVideoPlayAttemptInFlightRef.current = true;
      playZoomVideo(video);
    },
    [playZoomVideo]
  );

  const openZoomVideo = useCallback(
    (videoSrc) => {
      const nextInstanceId = zoomVideoOpenIdRef.current + 1;
      zoomVideoOpenIdRef.current = nextInstanceId;
      zoomVideoOpenedAtRef.current =
        typeof performance !== "undefined" &&
        typeof performance.now === "function"
          ? performance.now()
          : Date.now();
      pauseEmbeddedUserProfileVideos({ reset: false, reason: "open_viewer" });
      shouldAttemptZoomVideoPlayRef.current = true;
      zoomVideoPlayAttemptInFlightRef.current = false;
      setZoomVideoPlayFailed(false);

      flushSync(() => {
        setZoomVideo({
          url: videoSrc,
          instanceId: nextInstanceId,
        });
      });
    },
    [pauseEmbeddedUserProfileVideos]
  );

  const closeZoomVideo = useCallback(
    (options = {}) => {
      const reason = options.reason || "unspecified";
      const now =
        typeof performance !== "undefined" &&
        typeof performance.now === "function"
          ? performance.now()
          : Date.now();
      const elapsedSinceOpen = now - zoomVideoOpenedAtRef.current;

      if (
        reason === "backdrop" &&
        elapsedSinceOpen >= 0 &&
        elapsedSinceOpen < USER_PROFILE_VIDEO_BACKDROP_CLOSE_GUARD_MS
      ) {
        return;
      }

      shouldAttemptZoomVideoPlayRef.current = false;
      zoomVideoPlayAttemptInFlightRef.current = false;
      setZoomVideoPlayFailed(false);

      if (
        typeof document !== "undefined" &&
        document.fullscreenElement &&
        typeof document.exitFullscreen === "function"
      ) {
        try {
          const exitPromise = document.exitFullscreen();
          if (exitPromise && typeof exitPromise.catch === "function") {
            exitPromise.catch(() => {});
          }
        } catch {
          // Defensive only; the app viewer never requests native fullscreen.
        }
      }

      hardStopVideoElement(zoomVideoRef.current, "close_zoom_video");
      zoomVideoRef.current = null;
      cleanupUserProfileVideoMediaSession("close_zoom_video");
      setZoomVideo(null);
      zoomVideoOpenedAtRef.current = 0;
    },
    [cleanupUserProfileVideoMediaSession, hardStopVideoElement]
  );

  const handleUserProfileVideoTap = useCallback(
    (event, videoSrc) => {
      const previewVideo = getUserProfileVideoEventPreviewVideo(event);
      if (!mobileUserProfileVideoViewport) return;

      event.preventDefault();
      event.stopPropagation();
      pauseVideoElement(previewVideo, { reset: false });
      openZoomVideo(videoSrc);
    },
    [mobileUserProfileVideoViewport, openZoomVideo, pauseVideoElement]
  );

  const setZoomVideoNode = useCallback((node) => {
    zoomVideoRef.current = node;
  }, []);

  const handleZoomVideoReadyEvent = useCallback(
    () => {
      attemptZoomVideoPlay("viewer_ready");
    },
    [attemptZoomVideoPlay]
  );

  const handleZoomVideoPlayingEvent = useCallback(() => {
    shouldAttemptZoomVideoPlayRef.current = false;
    zoomVideoPlayAttemptInFlightRef.current = false;
    setZoomVideoPlayFailed(false);
  }, []);

  const handleZoomVideoFallbackPlay = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setZoomVideoPlayFailed(false);
      shouldAttemptZoomVideoPlayRef.current = true;
      zoomVideoPlayAttemptInFlightRef.current = false;
      attemptZoomVideoPlay("fallback_tap");
    },
    [attemptZoomVideoPlay]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const updateMobileUserProfileVideoViewport = () => {
      setMobileUserProfileVideoViewport(mediaQuery.matches);
    };

    updateMobileUserProfileVideoViewport();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener(
        "change",
        updateMobileUserProfileVideoViewport
      );
      return () => {
        mediaQuery.removeEventListener(
          "change",
          updateMobileUserProfileVideoViewport
        );
      };
    }

    if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(updateMobileUserProfileVideoViewport);
      return () => {
        mediaQuery.removeListener(updateMobileUserProfileVideoViewport);
      };
    }

    return undefined;
  }, []);

  useEffect(() => {
    if (!mobileUserProfileVideoViewport) {
      setMobilePreviewMetadataVideoIds((current) =>
        current.size === 0 ? current : new Set()
      );
      return undefined;
    }

    const videoPreviewIds = new Set();
    userPosts.forEach((post) => {
      const postId = String(post.id || "");
      if (isVideoPostMedia(post)) {
        videoPreviewIds.add(`post:${postId}`);
      }
      if (isVideoPostMedia(post.sharedPost)) {
        const sharedPostId = String(
          post.sharedPost?.id || `${postId || "post"}-shared`
        );
        videoPreviewIds.add(`shared:${postId}:${sharedPostId}`);
      }
    });

    setMobilePreviewMetadataVideoIds((current) => {
      const next = new Set(
        Array.from(current).filter((videoId) => videoPreviewIds.has(videoId))
      );
      return next.size === current.size ? current : next;
    });

    const previewEntries = Object.entries(mobileVideoPreviewRefs.current).filter(
      ([videoId, element]) => videoPreviewIds.has(videoId) && element
    );

    if (previewEntries.length === 0) return undefined;

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setMobilePreviewMetadataVideoIds((current) => {
        const next = new Set(current);
        previewEntries.forEach(([videoId]) => next.add(videoId));
        return next;
      });
      return undefined;
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        const visibleVideoIds = entries
          .filter((entry) => entry.isIntersecting || entry.intersectionRatio > 0)
          .map((entry) =>
            String(
              entry.target.getAttribute("data-userprofile-video-preview-id") ||
                ""
            )
          )
          .filter(Boolean);

        if (visibleVideoIds.length === 0) return;

        setMobilePreviewMetadataVideoIds((current) => {
          let changed = false;
          const next = new Set(current);
          visibleVideoIds.forEach((videoId) => {
            if (!next.has(videoId)) {
              next.add(videoId);
              changed = true;
            }
          });

          return changed ? next : current;
        });
      },
      {
        root: null,
        rootMargin: "600px 0px",
        threshold: 0.01,
      }
    );

    previewEntries.forEach(([, element]) => observer.observe(element));

    return () => observer.disconnect();
  }, [mobileUserProfileVideoViewport, userPosts]);

  useEffect(() => {
    if (!zoomVideo) return undefined;
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    const body = document.body;
    const documentElement = document.documentElement;
    const scrollContainer =
      userProfilePostsRef.current?.closest(".userprofile-container");
    if (!body || !documentElement) return undefined;

    const scrollY =
      window.scrollY ||
      window.pageYOffset ||
      documentElement.scrollTop ||
      body.scrollTop ||
      0;
    const scrollContainerTop = scrollContainer?.scrollTop || 0;
    const previousBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      overscrollBehavior: body.style.overscrollBehavior,
    };
    const previousDocumentStyles = {
      overflow: documentElement.style.overflow,
      overscrollBehavior: documentElement.style.overscrollBehavior,
    };
    const previousScrollContainerStyles = scrollContainer
      ? {
          overflow: scrollContainer.style.overflow,
          overscrollBehavior: scrollContainer.style.overscrollBehavior,
        }
      : null;

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    documentElement.style.overflow = "hidden";
    documentElement.style.overscrollBehavior = "none";
    if (scrollContainer) {
      scrollContainer.style.overflow = "hidden";
      scrollContainer.style.overscrollBehavior = "none";
    }

    return () => {
      body.style.position = previousBodyStyles.position;
      body.style.top = previousBodyStyles.top;
      body.style.left = previousBodyStyles.left;
      body.style.right = previousBodyStyles.right;
      body.style.width = previousBodyStyles.width;
      body.style.overflow = previousBodyStyles.overflow;
      body.style.overscrollBehavior = previousBodyStyles.overscrollBehavior;
      documentElement.style.overflow = previousDocumentStyles.overflow;
      documentElement.style.overscrollBehavior =
        previousDocumentStyles.overscrollBehavior;
      if (scrollContainer && previousScrollContainerStyles) {
        scrollContainer.style.overflow = previousScrollContainerStyles.overflow;
        scrollContainer.style.overscrollBehavior =
          previousScrollContainerStyles.overscrollBehavior;
        scrollContainer.scrollTop = scrollContainerTop;
      }

      try {
        window.scrollTo(0, scrollY);
      } catch {
        // Style restoration above still keeps the page usable.
      }
    };
  }, [zoomVideo]);

  return (
    <div className="userprofile-posts" ref={userProfilePostsRef}>
      <h3 className="userprofile-activity-heading">
        {t("profile_public_activity_title", {
          defaultValue: "{{name}}'s Fishing Activity",
          name: selectedUserData?.username || selectedUser,
        })}
      </h3>

      {userPosts.map((post) => {
        const postMediaSrc = resolvePostMediaSrc(post);
        const postHasVideoMedia = isVideoPostMedia(post);
        const sharedPostMediaSrc = resolvePostMediaSrc(post.sharedPost);
        const sharedPostHasVideoMedia = isVideoPostMedia(post.sharedPost);
        const postId = String(post.id || "");
        const sharedPostId = String(
          post.sharedPost?.id || `${postId || "post"}-shared`
        );
        const postVideoPreviewId = `post:${postId}`;
        const sharedPostVideoPreviewId = `shared:${postId}:${sharedPostId}`;
        const postVideoPoster = postHasVideoMedia
          ? resolveUserProfileVideoPoster(post)
          : { src: "", field: "" };
        const sharedPostVideoPoster = sharedPostHasVideoMedia
          ? resolveUserProfileVideoPoster(post.sharedPost)
          : { src: "", field: "" };
        const postVideoPreviewPreload = mobileUserProfileVideoViewport
          ? postVideoPoster.src
            ? "none"
            : mobilePreviewMetadataVideoIds.has(postVideoPreviewId)
            ? "metadata"
            : "none"
          : "metadata";
        const sharedPostVideoPreviewPreload = mobileUserProfileVideoViewport
          ? sharedPostVideoPoster.src
            ? "none"
            : mobilePreviewMetadataVideoIds.has(sharedPostVideoPreviewId)
            ? "metadata"
            : "none"
          : "metadata";
        const authorProgression = selectedUserData?.progression || post?.progression || {};
        const authorRankName = normalizeTextValue(
          authorProgression.currentRankName || authorProgression.rankName
        );
        const authorBadgeUrl = buildBadgeUrl(
          authorProgression.currentBadgeKey || authorProgression.badgeKey
        );
        const authorAvatarStyle = {
          objectPosition: resolveAvatarObjectPosition(selectedUserData),
          transform: `scale(${resolveAvatarScale(selectedUserData)})`,
        };
        const dateLabel = getPostDateLabel(post);
        const locationLabel = getPostLocationLabel(post);
        const postChips = buildPostChips(post, t);
        const likeCount = post.likes?.length || 0;
        const commentCount = post.comments?.length || 0;
        const currentUserHasLikedPost = Boolean(
          user?.id &&
            Array.isArray(post.likes) &&
            post.likes.some((likeUserId) => String(likeUserId) === String(user.id))
        );
        const postLikePending = isPostLikePending(post.id);
        const hasPostText = Boolean(post.sharedPost || hasMeaningfulValue(post.caption));
        const postContentClassName = [
          "rp-post__content",
          postMediaSrc ? "rp-post__content--with-media" : "",
          postMediaSrc && !hasPostText ? "rp-post__content--media-only" : "",
        ]
          .filter(Boolean)
          .join(" ");
        const postActionsClassName = [
          "post-actions",
          "rp-post__actions",
          post.postType !== "opinion"
            ? "rp-post__actions--has-info"
            : "rp-post__actions--no-info",
        ].join(" ");

        return (
          <div
            key={post.id}
            className="post rp-post rp-profile-post-card"
            ref={(node) => {
              if (node) {
                postRefs.current[post.id] = node;
              } else {
                delete postRefs.current[post.id];
              }
            }}
            tabIndex={-1}
          >
            <div className="userprofile-post-header rp-post__header">
              <button
                type="button"
                className="rp-post__avatar-button"
                disabled
                aria-label={t("profile_picture")}
              >
                <img
                  src={resolveUserAvatarSrc(selectedUserData)}
                  alt={t("profile_picture")}
                  className="rp-post__avatar"
                  style={authorAvatarStyle}
                />
              </button>

              <div className="rp-post__meta">
                <div className="rp-post__name-row">
                  <h3 className="userprofile-post-user rp-post__name">
                    {post.user}
                  </h3>
                  {authorBadgeUrl ? (
                    <img
                      src={authorBadgeUrl}
                      alt={
                        authorRankName
                          ? t("profile_rank_badge_alt", {
                              defaultValue: "{{rankName}} badge",
                              rankName: authorRankName,
                            })
                          : ""
                      }
                      className="rp-post__rank-badge"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  ) : null}
                </div>

                {authorRankName ? (
                  <span className="rp-post__rank-text">{authorRankName}</span>
                ) : null}
              </div>

              <div className="rp-post__header-trail">
                {dateLabel ? (
                  <span className="userprofile-post-date rp-post__time-row">
                    <span className="rp-post__time">{dateLabel}</span>
                  </span>
                ) : null}
                {locationLabel ? (
                  <span className="rp-post__location">
                    <MapPin size={11} strokeWidth={2.1} aria-hidden="true" />
                    <span>{locationLabel}</span>
                  </span>
                ) : null}
              </div>
            </div>

            <div className={postContentClassName}>
              {postMediaSrc ? (
                <div className="rp-post__image">
                  {postHasVideoMedia ? (
                    <div
                      className="rp-post__video-shell userprofile-post-video-shell"
                      ref={(element) => {
                        if (!postVideoPreviewId) return;
                        if (element) {
                          mobileVideoPreviewRefs.current[postVideoPreviewId] =
                            element;
                        } else {
                          delete mobileVideoPreviewRefs.current[
                            postVideoPreviewId
                          ];
                        }
                      }}
                      role={
                        mobileUserProfileVideoViewport ? "button" : undefined
                      }
                      tabIndex={mobileUserProfileVideoViewport ? 0 : undefined}
                      aria-label={
                        mobileUserProfileVideoViewport
                          ? t("open_video")
                          : undefined
                      }
                      onPointerUpCapture={(event) =>
                        handleUserProfileVideoTap(event, postMediaSrc)
                      }
                      onKeyDown={(event) => {
                        if (!mobileUserProfileVideoViewport) return;
                        if (event.key !== "Enter" && event.key !== " ") return;
                        handleUserProfileVideoTap(event, postMediaSrc);
                      }}
                      data-userprofile-video-preview-id={postVideoPreviewId}
                    >
                      <video
                        src={postMediaSrc}
                        poster={postVideoPoster.src || undefined}
                        controls={!mobileUserProfileVideoViewport}
                        controlsList="nodownload noplaybackrate"
                        disablePictureInPicture
                        playsInline
                        preload={postVideoPreviewPreload}
                        className="post-image clickable rp-post__media rp-post__media--video userprofile-post-video-preview"
                        data-userprofile-video-post-id={postVideoPreviewId}
                        onPlay={(event) => {
                          if (!mobileUserProfileVideoViewport) return;
                          pauseVideoElement(event.currentTarget, {
                            reset: false,
                          });
                        }}
                        onClick={(event) => {
                          if (mobileUserProfileVideoViewport) return;
                          event.stopPropagation();
                          handleOpenExpandedMedia({
                            type: "video",
                            src: postMediaSrc,
                          });
                        }}
                      />
                      {mobileUserProfileVideoViewport ? (
                        <span
                          className="userprofile-post-video-play"
                          aria-hidden="true"
                        ></span>
                      ) : null}
                    </div>
                  ) : (
                    <img
                      src={postMediaSrc}
                      alt={t("post_media")}
                      className="post-image clickable rp-post__media rp-post__media--image"
                      onClick={() =>
                        handleOpenExpandedMedia({
                          type: "image",
                          src: postMediaSrc,
                        })
                      }
                    />
                  )}
                </div>
              ) : null}

              {hasPostText ? (
                <div className="rp-post__text-panel">
                  {post.postType === "share" && post.sharedPost ? (
                    <div className="shared-post-wrapper rp-post__shared">
                      <p className="shared-by rp-post__shared-by">
                        <strong>{post.user}</strong> {t("shared")}
                      </p>

                      {post.caption ? (
                        <p className="shared-text rp-post__shared-text">
                          {post.caption}
                        </p>
                      ) : null}

                      <div className="shared-original rp-post__shared-original">
                        <strong>{post.sharedPost.user}</strong>

                        {post.sharedPost.postDate ? (
                          <span className="userprofile-post-date rp-post__shared-time">
                            {getPostDateLabel(post.sharedPost)}
                          </span>
                        ) : null}

                        <p>{post.sharedPost.caption}</p>

                        {sharedPostMediaSrc &&
                          (sharedPostHasVideoMedia ? (
                            <div
                              className="rp-post__video-shell userprofile-post-video-shell userprofile-post-video-shell--shared"
                              ref={(element) => {
                                if (!sharedPostVideoPreviewId) return;
                                if (element) {
                                  mobileVideoPreviewRefs.current[
                                    sharedPostVideoPreviewId
                                  ] = element;
                                } else {
                                  delete mobileVideoPreviewRefs.current[
                                    sharedPostVideoPreviewId
                                  ];
                                }
                              }}
                              role={
                                mobileUserProfileVideoViewport
                                  ? "button"
                                  : undefined
                              }
                              tabIndex={
                                mobileUserProfileVideoViewport ? 0 : undefined
                              }
                              aria-label={
                                mobileUserProfileVideoViewport
                                  ? t("open_video")
                                  : undefined
                              }
                              onPointerUpCapture={(event) =>
                                handleUserProfileVideoTap(
                                  event,
                                  sharedPostMediaSrc
                                )
                              }
                              onKeyDown={(event) => {
                                if (!mobileUserProfileVideoViewport) return;
                                if (
                                  event.key !== "Enter" &&
                                  event.key !== " "
                                ) {
                                  return;
                                }
                                handleUserProfileVideoTap(
                                  event,
                                  sharedPostMediaSrc
                                );
                              }}
                              data-userprofile-video-preview-id={
                                sharedPostVideoPreviewId
                              }
                            >
                              <video
                                src={sharedPostMediaSrc}
                                poster={sharedPostVideoPoster.src || undefined}
                                controls={!mobileUserProfileVideoViewport}
                                controlsList="nodownload noplaybackrate"
                                disablePictureInPicture
                                playsInline
                                preload={sharedPostVideoPreviewPreload}
                                className="post-image clickable rp-post__media rp-post__media--video userprofile-post-video-preview"
                                data-userprofile-video-post-id={
                                  sharedPostVideoPreviewId
                                }
                                onPlay={(event) => {
                                  if (!mobileUserProfileVideoViewport) return;
                                  pauseVideoElement(event.currentTarget, {
                                    reset: false,
                                  });
                                }}
                                onClick={(event) => {
                                  if (mobileUserProfileVideoViewport) return;
                                  event.stopPropagation();
                                  handleOpenExpandedMedia({
                                    type: "video",
                                    src: sharedPostMediaSrc,
                                  });
                                }}
                              />
                              {mobileUserProfileVideoViewport ? (
                                <span
                                  className="userprofile-post-video-play"
                                  aria-hidden="true"
                                ></span>
                              ) : null}
                            </div>
                          ) : (
                            <img
                              src={sharedPostMediaSrc}
                              alt={t("post_media")}
                              className="post-image clickable rp-post__media rp-post__media--image"
                              onClick={() =>
                                handleOpenExpandedMedia({
                                  type: "image",
                                  src: sharedPostMediaSrc,
                                })
                              }
                            />
                          ))}
                      </div>
                    </div>
                  ) : (
                    <TranslatablePostCaption
                      postId={post.id}
                      postType={post.postType}
                      text={post.caption}
                    />
                  )}
                </div>
              ) : null}
            </div>

            {postChips.length > 0 ? (
              <div className="rp-post__chips" aria-label={t("post_tags", { defaultValue: "Post tags" })}>
                {postChips.map((chip) => (
                  <span key={chip} className="rp-post__chip">
                    {chip}
                  </span>
                ))}
              </div>
            ) : null}

            <div className={postActionsClassName}>
              <button
                type="button"
                className={[
                  "rp-post__action-btn",
                  currentUserHasLikedPost ? "rp-post__action-btn--liked" : "",
                  postLikePending ? "rp-post__action-btn--pending" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                title={t("like")}
                aria-label={t("like")}
                onClick={() => handleLikePost(post)}
                disabled={postLikePending}
                aria-busy={postLikePending}
              >
                <span className="rp-post__action-icon" aria-hidden="true">
                  <Heart
                    size={24}
                    strokeWidth={2.2}
                    fill={currentUserHasLikedPost ? "currentColor" : "none"}
                  />
                </span>
                <span className="rp-post__action-copy rp-post__action-copy--desktop">
                  {t("like")} ({likeCount})
                </span>
                <span className="rp-post__action-count" aria-hidden="true">
                  {likeCount}
                </span>
              </button>

              <button
                type="button"
                className="rp-post__action-btn"
                title={t("comment")}
                aria-label={t("comment")}
                onClick={() => handleToggleComments(post.id)}
              >
                <span className="rp-post__action-icon" aria-hidden="true">
                  <MessageCircle size={24} strokeWidth={2.2} />
                </span>
                <span className="rp-post__action-copy rp-post__action-copy--desktop">
                  {t("comment")} ({commentCount})
                </span>
                <span className="rp-post__action-count" aria-hidden="true">
                  {commentCount}
                </span>
              </button>

              <button
                type="button"
                className="rp-post__action-btn rp-post__action-btn--share"
                title={t("share")}
                aria-label={t("share")}
                onClick={() => handleOpenShare(post)}
              >
                <span className="rp-post__action-icon" aria-hidden="true">
                  <Forward size={24} strokeWidth={2.25} />
                </span>
                <span className="rp-post__action-copy rp-post__action-copy--desktop">
                  {t("share")} ({getShareCountForPost(post, shareCountLookup)})
                </span>
              </button>

              {post.postType !== "opinion" && (
                <button
                  type="button"
                  className="rp-post__action-btn rp-post__action-btn--info"
                  title={t("info")}
                  aria-label={t("info")}
                  onClick={() => handleOpenInfo(post)}
                >
                  <PostInfoActionIcon />
                  <span className="rp-post__action-copy rp-post__action-copy--desktop">
                    {t("info")}
                  </span>
                </button>
              )}
            </div>
            {openComments === post.id && (
              <div className="post-comments rp-post__comments">
                {(post.comments || []).map((comment) => {
                  const commentUser = usersMap[comment.userId];
                  const commentKey = `${post.id}:${comment.id}`;

                  return (
                    <div
                      key={comment.id}
                      className={`post-comment${
                        highlightedCommentKey === commentKey
                          ? " comment-focus-target"
                          : ""
                      }`}
                      ref={(node) => {
                        if (node) {
                          commentRefs.current[commentKey] = node;
                        } else {
                          delete commentRefs.current[commentKey];
                        }
                      }}
                      tabIndex={-1}
                    >
                    <div className="post-comment-header">
                      <img
                        src={
                          commentUser?.avatar
                            ? `${API_BASE}${commentUser.avatar}`
                            : "https://cdn-icons-png.flaticon.com/512/847/847969.png"
                        }
                        alt={t("profile_picture")}
                        className="post-comment-avatar"
                      />
                      <strong>{comment.username}</strong>
                    </div>

                    <div className="post-comment-text">{comment.text}</div>

                    <div className="post-comment-actions">
                      <button
                        className="comment-like-btn"
                        onClick={() => handleLikeComment(post.id, comment.id)}
                      >
                        {t("like")} ({comment.likes?.length || 0})
                      </button>

                      <button
                        className="comment-reply-btn"
                        onClick={() => handleToggleReply(comment.id)}
                      >
                        {t("reply")}
                      </button>
                    </div>

                    {(comment.replies || []).map((reply) => (
                      <div key={reply.id} className="post-reply">
                        <div className="post-reply-text">
                          <strong>{reply.username}</strong> {reply.text}
                        </div>
                        <button
                          className="reply-like-btn"
                          onClick={() =>
                            handleLikeReply(post.id, comment.id, reply.id)
                          }
                        >
                          {t("like")} ({reply.likes?.length || 0})
                        </button>
                      </div>
                    ))}

                    {replyTo === comment.id && (
                      <div className="post-reply-form">
                        <input
                          type="text"
                          value={replyText}
                          placeholder={t("write_reply")}
                          onChange={(event) => setReplyText(event.target.value)}
                        />
                        <button
                          onClick={() => handleSubmitReply(post.id, comment.id)}
                        >
                          {t("send")}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="post-comment-form">
                <input
                  type="text"
                  value={commentText}
                  placeholder={t("write_comment")}
                  onChange={(event) => setCommentText(event.target.value)}
                />
                <button onClick={() => handleSubmitComment(post.id)}>
                  {t("send")}
                </button>
              </div>
            </div>
          )}
          </div>
        );
      })}
      <PaginationSentinel
        enabled={Boolean(selectedUserData?.id) && profilePostsHasMore}
        loading={profilePostsLoadingMore}
        onLoadMore={loadMoreProfilePosts}
        loadingLabel={t("loading", { defaultValue: "Loading…" })}
        testId="public-profile-pagination-sentinel"
      />
      {zoomVideo ? (
        <div
          className="userprofile-post-video-viewer"
          onClick={(event) => {
            if (event.target !== event.currentTarget) return;
            closeZoomVideo({ reason: "backdrop" });
          }}
        >
          <button
            type="button"
            className="userprofile-post-video-viewer__close"
            aria-label={t("close")}
            onClick={(event) => {
              event.stopPropagation();
              closeZoomVideo({ reason: "close_button" });
            }}
          >
            {"\u2715"}
          </button>

          <video
            key={`${zoomVideoInstanceId}:${zoomVideoUrl}`}
            ref={setZoomVideoNode}
            src={zoomVideoUrl}
            controls
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture
            playsInline
            autoPlay
            preload="auto"
            className="userprofile-post-video-viewer__video"
            onLoadedMetadata={handleZoomVideoReadyEvent}
            onCanPlay={handleZoomVideoReadyEvent}
            onPlaying={handleZoomVideoPlayingEvent}
            onClick={(event) => event.stopPropagation()}
          />
          {zoomVideoPlayFailed ? (
            <button
              type="button"
              className="userprofile-post-video-viewer__fallback"
              onClick={handleZoomVideoFallbackPlay}
            >
              {t("tap_to_play")}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
